import { useTranslation } from '@/i18n/hook';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { buildArticleSchema, buildFaqPageSchema } from '@/seo/schema';
import { pillarArticles } from '@/mocks/articles';

export default function PillarArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const currentLang = i18n.language;

  const article = pillarArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 font-sans">
        <Navbar />
        <main className="pt-16 flex items-center justify-center py-24">
          <div className="text-center">
            <p className="text-foreground-600 mb-4">{t('article.notFound')}</p>
            <Link to="/" className="text-primary-700 font-medium hover:text-primary-800">
              {t('article.back')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const get = (l: { pt: string; en: string; de: string }) => {
    if (currentLang === 'de') return l.de;
    if (currentLang === 'en') return l.en;
    return l.pt;
  };

  const jsonLd = [
    buildArticleSchema({
      title: get(article.title),
      description: get(article.meta),
      image: article.image,
      path: `/article/${article.slug}`,
      datePublished: '2026-08-30',
      dateModified: '2026-08-30',
    }),
    buildFaqPageSchema(article.faq.map((item) => ({ q: get(item.q), a: get(item.a) }))),
  ];

  return (
    <div className="min-h-screen bg-background-50 font-sans">
      <JsonLd data={jsonLd} />
      <Navbar />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.image}
              alt={get(article.title)}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/85 to-background-50/60" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                {get(article.category)}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {get(article.title)}
            </h1>
            <p className="text-foreground-600 text-lg leading-relaxed mb-6">
              {get(article.meta)}
            </p>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{t('article.author')}</span>
              <span>&bull;</span>
              <span>{t('article.published')}</span>
              <span>&bull;</span>
              <span>{t('article.updated')}</span>
            </div>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-20">
          {/* Intro */}
          <p className="text-foreground-700 leading-relaxed text-lg mb-10">{get(article.intro)}</p>

          {/* Sections */}
          <div className="space-y-10">
            {article.sections.map((section, i) => (
              <div key={i}>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                  {get(section.heading)}
                </h2>
                <p className="text-foreground-700 leading-relaxed">{get(section.body)}</p>
              </div>
            ))}
          </div>

          {/* Key Takeaways */}
          <div className="mt-12 bg-primary-50/70 rounded-xl p-6 border border-primary-200">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">{t('article.takeaways')}</h2>
            <ul className="space-y-3">
              {article.takeaways.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-xs font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-foreground-700">{get(item)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ */}
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">{t('article.faq')}</h2>
            <div className="space-y-4">
              {article.faq.map((item, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                  <h3 className="font-semibold text-foreground-800 mb-2">{get(item.q)}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{get(item.a)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-background-100 rounded-xl p-8 border border-background-200/70 text-center">
            <p className="text-foreground-700 mb-4">{t('final.text')}</p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('cta.main1')}
            </a>
          </div>

          {/* Internal link back to home */}
          <div className="pt-6 border-t border-background-200/70">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('article.back')}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}